/**
 * HealthMemorialView — what /health renders for a bereaved family (#693).
 *
 * ── Why the whole surface is replaced rather than filtered ────────────────
 *
 * /tasks and /calendar could be gated row by row: a bereaved family still has
 * real open business with TRF (equipment returns, grant paperwork), so the
 * memorial gate there removes treatment-era items and leaves the surface.
 * /health has no such remainder. Every one of its four sections — upcoming
 * appointments, active/inactive medications, questions for the doctor, the
 * symptom log — is a treatment activity, and every one of its empty states is
 * written as an invitation to start one ("Tap Log symptom to capture how the
 * day is going", "Add a medication so {companion} can track refills"). Filter
 * the content and what survives is four prompts asking a bereaved parent to
 * log treatment for a child who has died. The empty page is the worse screen,
 * not the safer one, which is why this view says something instead.
 *
 * ── Why it does not render the record read-only ───────────────────────────
 *
 * That was the other candidate and it is a defensible product, but it is not
 * this page: every affordance here (add, toggle active, mark answered, log
 * reaction, refill-soon alerts, the "severe symptom unreported → draft a
 * message" banner) is a write path, and a read-only variant of four sections
 * is a new surface, not a gate. Until that exists the honest thing to say is
 * that the record is kept, which is true — the documents are untouched and
 * the family's TRFer can still see them on the staff profile. So this view
 * makes no promise about who will send what: it states what happened to the
 * page, and stops.
 *
 * The hero reuses `getMemorialHero`, the same copy the dashboard shows, so a
 * bereaved family does not meet two different memorial registers one nav click
 * apart.
 *
 * ── DRAFT COPY — PENDING TRF TONE REVIEW (#693) ───────────────────────────
 * TRF owns the final wording of the two strings below, in the posture #644
 * set for the memorial hero (docs/DECISIONS.md 2026-08-11): an empty
 * suppression is also a tone, and a page of logging prompts was the worse
 * draft. Add them to the parent-side section of
 * docs/superpowers/memos/2026-08-11-memorial-copy-review.md when the memo is
 * next revised, and remove this banner when TRF answers.
 *
 * #2109 R3 (lane L3): the two strings are `health:memorial.*`, the named
 * possessive one whole key. Like the `memorial` namespace (R3-B7) their keys
 * were on the PM's force-draft list, because the English itself is
 * unreviewed; Victor reviewed each translation on 2026-09-30 (`reviewed`).
 */
import { useTranslation } from 'react-i18next';
import { PageHead } from '@/components/editorial';
import { Card } from '@/components/ui/card';
import { getMemorialHero } from '@/lib/dashboard-mode';

interface HealthMemorialViewProps {
  /** `family.patient.name`. Absent on an incomplete record; the hero degrades
   *  to "Your child" and the body drops the possessive rather than printing an
   *  empty one. */
  childName?: string | null;
}

export function HealthMemorialView({ childName }: HealthMemorialViewProps) {
  // `getMemorialHero` reads the live language; this hook re-renders on a switch.
  const { t } = useTranslation(['health', 'common']);
  const hero = getMemorialHero(childName);
  const name = (childName ?? '').trim();

  return (
    <div className="space-y-6">
      <PageHead eyebrow={hero.eyebrow} title={hero.title} lede={hero.lede} />

      <Card className="px-6 py-8">
        <p className="font-serif text-title text-foreground mb-2">
          {t('health:memorial.closed')}
        </p>
        <p className="text-body text-stevens-ink-3 leading-relaxed max-w-prose">
          {name ? t('health:memorial.bodyNamed', { name }) : t('health:memorial.body')}
        </p>
      </Card>
    </div>
  );
}
